'use client';

import { Button } from '@ahafood/ui/components/button';
import { Card } from '@ahafood/ui/components/card';
import { Image } from '@ahafood/ui/components/image';
import { Separator } from '@ahafood/ui/components/separator';
import { assetPrefix } from '@ahm/api-wrappers-core';
import { cn, JSFunction } from '@ahm/common-helpers';
import { getCookie } from 'cookies-next';
import { ArrowLeft } from 'lucide-react';
import { useParams, useSearchParams } from 'next/navigation';
import { useEffect, useRef } from 'react';
import { PersonalizeItemSkeleton } from '../for-you-item-list';
import SolomealHeader from '../solomeal-header';
import { NewYearPersonalizeItemCard } from './new-year-personalize-card';
import { useGetPersonalize } from '@/api/use-get-personalize';
import NotificationDemo from '@/app/[locale]/(main)/store/components/store-notification';
import NewYearAnimation from '@/components/animations/new-year-animation';
import UserButton from '@/components/user/user-button';
import { useStoreCitySheet } from '@/contexts/store-city-sheet-context';
import { useCityRoutes } from '@/hooks/use-city-navigation';
import { usePathname } from '@/i18n/routing';
import MainHeader from '@/layouts/main/main-header';
import { clearDebugLogs } from '@/lib/debug/use-debug-tracker';
import { trackEvent } from '@/lib/event-tracking';
import { useIsMobile } from '@/lib/is-mobile';
import { useDetectWebInApp } from '@/lib/is-webinapp/use-detect-webinapp';
import { useAuth } from '@/providers/auth-provider';
import { CITIES, useAiFoodOrderStore, useAuthStore } from '@/store';

// eslint-disable-next-line sonarjs/cognitive-complexity
export default function LunarNewYearHeader() {
  const params = useParams();
  const searchParams = useSearchParams();
  const latParam = searchParams.get('lat');
  const lngParam = searchParams.get('lng');
  const address = useAiFoodOrderStore.use.address();
  const citySlug = Array.isArray(params.city) ? params.city[0] : params.city;
  const cityData = CITIES.find((c) => c.slug === citySlug);
  const cityLabel = cityData?.label ?? citySlug;
  const cityId = cityData?.value ?? 'SGN';
  const routes = useCityRoutes();
  const pathName = usePathname();
  const isMobile = useIsMobile();
  const isWebInApp = useDetectWebInApp();
  const headerRef = useRef<HTMLDivElement>(null);

  const addressCookie = getCookie('ahafood_address');
  const isHomepage = pathName === routes.home;
  const { isLoading } = useAuth();

  const { openCitySheet, isNavigating } = useStoreCitySheet();
  const { user } = useAuthStore();
  const personalParams = {
    customer_id: user?.cunstomerId ?? '',
    city_id: cityId ?? 'SGN',
    lat: address?.lat ?? 10.7769,
    lng: address?.lng ?? 106.7009,
    limit: 10,
  };

  const { data: personalizeData, isLoading: isPersonalizeLoading } =
    useGetPersonalize(personalParams, {
      enabled: !!user?.cunstomerId && !!cityId,
      retry: 1,
    });

  const logBackButtonClick = () => {
    trackEvent('click_button', {
      action_time: Math.floor(Date.now() / 1000),
      button_name: 'is_back',
      button_location: 'home',
      timestamp: Date.now(),
      user_id: user?.cunstomerId,
    });
  };

  const handleToggleAmori = () => {
    if (
      typeof window !== 'undefined' &&
      typeof window.setOpenChange === 'function'
    ) {
      window.setOpenChange(true);
      trackEvent('chatbot_event', {
        action_time: Math.floor(Date.now() / 1000),
        button_name: 'chatbot_banner',
        timestamp: Date.now(),
        user_id: user?.cunstomerId,
      });
    }
  };

  useEffect(() => {
    if (
      !addressCookie &&
      !address &&
      !isNavigating &&
      !isLoading &&
      !latParam &&
      !lngParam
    ) {
      openCitySheet();
    }
  }, [
    addressCookie,
    address,
    isNavigating,
    isLoading,
    openCitySheet,
    latParam,
    lngParam,
  ]);

  if (pathName.includes('/danh-sach-cua-hang')) {
    return null;
  }

  const isSolomealPage =
    pathName.includes('/solomeal') && !pathName.includes('/danh-sach-cua-hang');

  if (isSolomealPage) {
    return <SolomealHeader />;
  }

  if (!isMobile) {
    return <MainHeader />;
  }

  // Mobile version - Lunar New Year theme
  return (
    <>
      <NotificationDemo />
      <NewYearAnimation containerRef={headerRef} />
      <div
        ref={headerRef}
        className="relative overflow-hidden px-4 pb-6 flex flex-col overflow-x-clip"
      >
        {/* Lunar New Year gradient background */}
        <div
          className="absolute inset-0 -z-10"
          style={{
            backgroundImage: `url(${assetPrefix}/static/images/new-year-bg.webp)`,
            backgroundPosition: 'top center',
            backgroundRepeat: 'no-repeat',
            backgroundSize: '100% auto',
            // background:
            //   'linear-gradient(180deg, #D4341C 0%, #E8522A 25%, #F47B3A 50%, #ffc694 75%, #fff0db 100%)',
          }}
        />

        <header
          className={cn(
            'mx-auto flex flex-col h-auto w-full max-w-screen-xl md:gap-4 relative z-20'
          )}
        >
          {/* Location bar */}
          <div className="py-6 relative pt-24">
            <Card
              className={cn(
                'w-full rounded-full p-2 shadow-md cursor-pointer text-left flex gap-2 items-center border border-white/50 transition-colors relative overflow-visible',
                isWebInApp ? 'justify-start' : 'justify-between pl-4'
              )}
              style={{
                background:
                  'linear-gradient(90deg, rgba(255, 250, 234, 0.95) 0%, rgba(254, 243, 224, 0.95) 100%)',
              }}
              onClick={() => openCitySheet()}
            >
              {isWebInApp && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    logBackButtonClick();
                    clearDebugLogs();
                    JSFunction.call({ name: 'close' });
                  }}
                  className="flex size-10 items-center justify-center rounded-full bg-[#fdce6f] backdrop-blur-sm"
                >
                  <ArrowLeft className="size-6 text-[#c78630]" />
                </button>
              )}
              <div className="flex gap-1 hover:bg-none bg-transparent text-left px-0 flex-1 min-w-0 items-start">
                <Image
                  width={28}
                  height={28}
                  className="shrink-0"
                  src={`${assetPrefix}/static/icons/location.svg`}
                  alt={'Location Icon'}
                />
                <div className="flex flex-col gap-0 flex-1 min-w-0 pr-16">
                  <span className="text-neutral-90 text-sm font-bold flex items-center">
                    Giao món đến
                  </span>
                  <div className="flex items-center">
                    <span className="text-[#d53311] text-xs font-semibold line-clamp-2">
                      {address?.address ?? cityLabel}
                    </span>
                  </div>
                </div>
              </div>

              {/* Red envelopes decoration on the right */}
              <div className="absolute -top-10 -right-2 size-28 z-10 pointer-events-none">
                <Image
                  src={`${assetPrefix}/static/images/bao-li-xi.webp`}
                  alt="Red Envelope"
                  fill
                  className="object-contain"
                />
              </div>

              <div
                role="button"
                className="flex items-center shrink-0"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.stopPropagation();
                  }
                }}
                tabIndex={0}
              >
                {!isWebInApp ? <UserButton /> : null}
              </div>
            </Card>
          </div>

          {/* Lunar New Year Title - Outside the card */}
          <div className="flex flex-col gap-1 mb-3 px-1 text-[#d53311]">
            <h2 className="text-[40px] font-bold leading-tight">
              Gợi ý từ Amori
            </h2>
            <p className="text-sm">
              AI giúp bạn đỡ phải nghĩ, gợi món nhanh trong 10s
            </p>
          </div>
          {/* Content - either Personalize Items or AI Chat Card */}
          {isPersonalizeLoading ? (
            <div className="w-full p-4">
              <PersonalizeItemSkeleton />
            </div>
          ) : personalizeData?.items &&
            personalizeData.debug.is_new_user === false &&
            personalizeData.items.length > 0 &&
            isHomepage ? (
            // Show personalized item card
            <div className="w-full flex overflow-x-scroll scrollbar-hide gap-4">
              {personalizeData.items.map((item) => (
                <div
                  key={item._id}
                  className="shrink-0 w-[calc(100vw-36px)] pb-4"
                >
                  <NewYearPersonalizeItemCard
                    location="amori_home_zone"
                    item={item}
                    isAuthenticated={!!user?.cunstomerId}
                    // eslint-disable-next-line @typescript-eslint/no-empty-function
                    setShowLoginDialog={() => {}}
                  />
                </div>
              ))}
            </div>
          ) : (
            // Show AI Chat prompt
            <Card
              onClick={(e) => {
                e.stopPropagation();
                handleToggleAmori();
              }}
              className=" flex flex-col gap-0 bg-[#fff5df] px-4 pb-4 mt-8 rounded-3xl pt-6 shadow-md cursor-pointer text-left items-center border border-white transition-colors relative"
            >
              <span className="text-lg font-medium w-full text-left">
                Chat với{' '}
                <span className="bg-gradient-to-r from-[#0D4073] via-[#FD74FF] to-[#FF7F32] bg-clip-text text-transparent font-bold">
                  Amori
                </span>{' '}
                tại đây...
              </span>
              <div className="flex gap-3 w-full items-center">
                <div className="flex flex-col gap-1 flex-1">
                  <Separator
                    orientation="vertical"
                    className="h-0.5 bg-gradient-to-r from-[#3342FF00] via-[#f4af37] to-[#ff3333] w-full"
                  />
                  <span className="font-normal text-xs">
                    Người bạn AI thiết kế bữa ngon cho bạn
                  </span>
                </div>
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleAmori();
                  }}
                  className="relative px-6 py-2.5 font-bold text-white rounded-2xl overflow-hidden flex items-center gap-2 bg-gradient-to-b from-[#f46737] to-[#ed431b] shadow-lg hover:bg-[#ed431b] z-10"
                >
                  {/* Inner shadows */}
                  <div
                    className="absolute inset-0 rounded-2xl pointer-events-none"
                    style={{
                      boxShadow:
                        'inset 0px 0px 5px 0px #FFACE4, inset 0px 0px 5px 0px #FFACE4',
                    }}
                  />
                  {/* Overlay blend */}
                  <div
                    className="absolute inset-0 rounded-2xl pointer-events-none"
                    style={{
                      background: 'rgba(254, 200, 241, 0)',
                      mixBlendMode: 'overlay',
                    }}
                  />
                  {/* Star Icon */}
                  <Image
                    src={`${assetPrefix}/static/icons/star.svg`}
                    alt="AI Icon"
                    width={16}
                    height={16}
                  />
                  {/* Text */}
                  Hỏi AI
                </Button>
              </div>
              <div
                className="absolute right-0 -top-10 size-28 flex items-center justify-center"
                style={{
                  backgroundImage:
                    'url(/static/icons/amori-avt-header-case.svg)',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  backgroundSize: 'contain',
                }}
              >
                <div className="size-16 relative mb-5">
                  <Image
                    src={`${assetPrefix}/static/images/amori-avt.webp`}
                    fill
                    alt={'amori'}
                  />
                </div>
              </div>
            </Card>
          )}
        </header>
      </div>
    </>
  );
}
